import { Helmet } from "react-helmet-async";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Check, TrendingUp, Target, Users, BarChart3, MousePointerClick, Clock, Award, Megaphone, Globe } from "lucide-react";
import { Header } from "@/components/Header";
import { KnowledgeConnections } from "@/components/knowledge/KnowledgeConnections";
import { Footer } from "@/components/Footer";
import { BreadcrumbNav } from "@/components/BreadcrumbNav";
import { EnhancedStructuredData } from "@/components/EnhancedStructuredData";
import { SEOArticleStructure } from "@/components/SEOArticleStructure";
import { useSEOOptimization } from "@/hooks/useSEOOptimization";
import { generateCanonicalUrl } from "@/utils/seoHelpers";

const GoogleAdsTheWoodlandsPage = () => {
  const [language, setLanguage] = useState<'es' | 'en'>('es');
  const toggleLanguage = () => setLanguage(language === 'es' ? 'en' : 'es');
  
  useSEOOptimization();
  const canonicalUrl = generateCanonicalUrl('/google-ads-the-woodlands');

  const services = [
    {
      icon: Target,
      title: "Análisis de Audiencia & Keywords",
      description: "Keywords de alta intención para The Woodlands con enfoque bilingüe (ES/EN)."
    },
    {
      icon: Megaphone,
      title: "Configuración Completa de Google Ads",
      description: "Campañas Search, Display, Remarketing y YouTube optimizadas."
    },
    {
      icon: MousePointerClick,
      title: "Optimización de Landing Pages + CRO",
      description: "Texto, diseño, formularios y llamadas a la acción que convierten."
    },
    {
      icon: Globe,
      title: "Segmentación Local Avanzada",
      description: "Geolocalización, dispositivo, horario e idioma precisos."
    },
    {
      icon: BarChart3,
      title: "Tracking y Atribución",
      description: "Google Ads + GA4 + call tracking + formularios integrados."
    },
    {
      icon: Award,
      title: "Reportes Transparentes",
      description: "CTR, CPC, CPA, ROAS y leads generados mensualmente."
    }
  ];

  const process = [
    {
      day: "Día 0-10",
      title: "Auditoría & KPIs",
      description: "Revisión de campañas actuales, auditoría completa y definición de objetivos."
    },
    {
      day: "Día 11-30",
      title: "Configuración & Tracking",
      description: "Nueva estructura de campañas, landing pages y sistemas de seguimiento."
    },
    {
      day: "Día 31-60",
      title: "Pruebas A/B & Optimización",
      description: "Tests de anuncios, segmentación, ajuste de pujas y conversiones."
    },
    {
      day: "Día 61-90",
      title: "Escalado & Integración",
      description: "Expansión de campañas exitosas, nuevas audiencias y remarketing."
    }
  ];

  const results = [
    "Clientes potenciales cualificados desde búsquedas de alto valor",
    "Mejor control del gasto publicitario con optimización continua",
    "Integración con métricas reales de negocio (llamadas, ventas, formularios)",
    "Ventaja competitiva local con estrategia bilingüe adaptada a The Woodlands"
  ];

  const sectors = [
    "Servicios a Domicilio (Plomería, HVAC, Remodelación)",
    "Salud y Bienestar",
    "Despachos Legales",
    "E-commerce Local",
    "Real Estate",
    "Restaurantes Bilingües"
  ];

  const plans = [
    {
      name: "Inicio PPC",
      duration: "Setup inicial",
      description: "Configuración completa para empezar con bases sólidas y resultados rápidos.",
      features: [
        "Setup + 2 campañas",
        "Tracking completo",
        "1 landing page optimizada",
        "Reporte mensual"
      ]
    },
    {
      name: "Crecimiento PPC",
      duration: "Mensual",
      description: "Escalado estratégico con múltiples campañas y optimización constante.",
      features: [
        "Hasta 5 campañas activas",
        "Remarketing incluido",
        "Optimización mensual",
        "A/B testing continuo"
      ]
    },
    {
      name: "Suite Performance",
      duration: "Mensual",
      description: "PPC + SEO integrados para máximo alcance y conversión.",
      features: [
        "Todo Crecimiento PPC",
        "SEO local incluido",
        "Remarketing avanzado",
        "Reporting unificado"
      ]
    }
  ];

  const faqs = [
    {
      question: "¿Cuándo veré resultados con Google Ads?",
      answer: "Normalmente ves leads en los primeros 30–60 días; la optimización continua hace que los resultados mejoren a mediano plazo (90+ días)."
    },
    {
      question: "¿Tengo que comprometerme con contrato largo?",
      answer: "Ofrecemos planes flexibles. Lo importante es la optimización constante y el compromiso con tus métricas de negocio."
    },
    {
      question: "¿Por qué elegir una agencia bilingüe?",
      answer: "En The Woodlands muchas empresas tienen audiencias hispanohablantes e inglesas. Un mensaje adaptado al idioma correcto mejora significativamente la tasa de conversión."
    },
    {
      question: "¿Cómo medimos el retorno de la inversión (ROI)?",
      answer: "Medimos llamadas, formularios, ventas, coste por cliente y retorno de cada campaña. No solo clics ni impresiones, sino resultados reales de negocio."
    }
  ];

  return (
    <>
      <Helmet>
        <html lang={language} />
        <title>Agencia Google Ads en The Woodlands | PPC que convierte – Luis ROC</title>
        <meta 
          name="description" 
          content="Aumenta tus clientes en The Woodlands con campañas de Google Ads y PPC optimizadas. Estrategia bilingüe, seguimiento de leads y ROI real con Luis ROC." 
        />
        <meta name="keywords" content="google ads the woodlands, ppc the woodlands, publicidad google the woodlands, agencia google ads, campañas ppc bilingües, marketing digital the woodlands" />
        <link rel="canonical" href={canonicalUrl} />
        
        {/* Open Graph */}
        <meta property="og:title" content="Agencia Google Ads en The Woodlands | Luis ROC" />
        <meta property="og:description" content="Campañas de PPC que convierten con estrategia bilingüe y medición orientada a negocio." />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="es_US" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Agencia Google Ads en The Woodlands | Luis ROC" />
        <meta name="twitter:description" content="PPC optimizado y orientado a convertir demanda en oportunidades medibles en The Woodlands." />
      </Helmet>

      <EnhancedStructuredData
        service={{
          name: "Agencia Google Ads The Woodlands",
          description: "Gestión profesional de campañas Google Ads y PPC para empresas en The Woodlands, Texas",
          provider: "Luis ROC",
          areaServed: ["The Woodlands", "Houston", "Spring", "Conroe", "Montgomery County"],
        }}
        faqs={faqs}
        webpage={{
          name: "Google Ads & PPC en The Woodlands",
          description: "Servicios profesionales de Google Ads y PPC en The Woodlands, TX. Campañas optimizadas para conversión.",
          url: canonicalUrl,
          keywords: ["google ads the woodlands", "ppc", "publicidad digital", "campañas bilingües"]
        }}
      />

      <div className="min-h-screen bg-background">
        <Header language={language} onLanguageChange={toggleLanguage} />
        
        <main>
          <SEOArticleStructure
            title="Campañas de Google Ads & PPC en The Woodlands que impulsan conversiones"
            description="Gestión profesional de campañas Google Ads en The Woodlands. Estrategia bilingüe con seguimiento completo de ROI."
            author="Luis ROC"
            keywords={["google ads the woodlands", "ppc", "publicidad digital"]}
            className="container mx-auto px-4 py-8"
          >
            {/* Breadcrumb Navigation */}
            <BreadcrumbNav 
              items={[
                { name: "Servicios", url: "/servicios" },
                { name: "Google Ads The Woodlands", url: "/google-ads-the-woodlands" }
              ]}
              language="es"
            />

            {/* Hero Section */}
            <section className="py-16 text-center">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
                Campañas de Google Ads & PPC en The Woodlands que impulsan conversiones
              </h1>
              
              <div className="max-w-4xl mx-auto mb-8">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  En Luis ROC diseñamos y gestionamos campañas de Google Ads / PPC en The Woodlands para empresas que quieren 
                  ir más allá de "clics" y obtener clientes reales. Nuestra propuesta bilingüe (ES/EN) une creatividad, datos 
                  y optimización constante para asegurar que cada dólar invertido tenga un retorno medible. Trabajamos con 
                  públicos locales en The Woodlands y alrededores, optimizando desde la palabra clave hasta la landing page final, 
                  con seguimiento de llamadas, formularios y visitas. ¿Listo para dejar de gastar en anuncios que no convierten? 
                  Hablemos y te muestro cómo.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => window.location.href = '/diagnostico-seo-gratis'}
                  className="text-lg px-8"
                >
                  Agenda tu reunión inicial gratuita de 30 minutos
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  onClick={() => window.location.href = '/contacto'}
                  className="text-lg px-8"
                >
                  Agenda una Consulta
                </Button>
              </div>
            </section>

            {/* Services Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  ¿Qué incluye nuestro servicio de PPC?
                </h2>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {services.map((service, index) => {
                    const Icon = service.icon;
                    return (
                      <Card key={index} className="p-6 hover:shadow-lg transition-shadow">
                        <Icon className="h-12 w-12 text-primary mb-4" />
                        <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                        <p className="text-muted-foreground">{service.description}</p>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </section>

            {/* Process Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Nuestro proceso en 90 días
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Reportes con insights, propuestas de mejora y roadmap para el siguiente trimestre
              </p>
              
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {process.map((step, index) => (
                  <Card key={index} className="p-6 relative">
                    <div className="absolute -top-4 left-6 bg-primary text-primary-foreground w-8 h-8 rounded-full flex items-center justify-center font-bold">
                      {index + 1}
                    </div>
                    <div className="flex items-center gap-2 mb-3 mt-2">
                      <Clock className="h-5 w-5 text-primary" />
                      <span className="font-semibold text-primary">{step.day}</span>
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{step.title}</h3>
                    <p className="text-muted-foreground">{step.description}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* Results Section */}
            <section className="py-16 bg-primary/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                  Resultados que puedes esperar
                </h2>
                
                <div className="max-w-3xl mx-auto space-y-4">
                  {results.map((result, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 bg-background rounded-lg">
                      <Check className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
                      <p className="text-lg">{result}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Sectors Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                Sectores que servimos en The Woodlands
              </h2>
              <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                Adaptamos mensaje, ubicación e idioma para cada nicho específico
              </p>
              
              <div className="flex flex-wrap justify-center gap-4">
                {sectors.map((sector, index) => (
                  <div 
                    key={index}
                    className="px-6 py-3 bg-accent text-accent-foreground rounded-full font-medium hover:bg-accent/80 transition-colors"
                  >
                    {sector}
                  </div>
                ))}
              </div>
            </section>

            {/* Plans Section */}
            <section className="py-16 bg-accent/5 rounded-lg mb-16">
              <div className="container mx-auto px-4">
                <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
                  Planes y precios
                </h2>
                <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
                  Solicita propuesta personalizada según tu presupuesto y objetivos
                </p>
                
                <div className="grid md:grid-cols-3 gap-8">
                  {plans.map((plan, index) => (
                    <Card key={index} className="p-8 hover:shadow-xl transition-shadow">
                      <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                      <p className="text-primary font-semibold mb-4">{plan.duration}</p>
                      <p className="text-muted-foreground mb-6">{plan.description}</p>
                      
                      <ul className="space-y-3 mb-6">
                        {plan.features.map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <Button 
                        className="w-full"
                        onClick={() => window.location.href = '/contacto'}
                      >
                        Solicitar Propuesta
                      </Button>
                    </Card>
                  ))}
                </div>
              </div>
            </section>

            {/* FAQ Section */}
            <section className="py-16 mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
                Preguntas Frecuentes
              </h2>
              
              <div className="max-w-3xl mx-auto space-y-6">
                {faqs.map((faq, index) => (
                  <Card key={index} className="p-6">
                    <h3 className="text-xl font-semibold mb-3">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </Card>
                ))}
              </div>
            </section>

            {/* CTA Section */}
            <section className="py-16 bg-gradient-to-r from-primary to-primary-glow text-primary-foreground rounded-lg text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Transforma tus anuncios en clientes reales
              </h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
                Reunión inicial gratuita de 30 minutos para revisar qué necesita tu empresa
              </p>
              <Button 
                size="lg"
                variant="secondary"
                onClick={() => window.location.href = '/diagnostico-seo-gratis'}
                className="text-lg px-8"
              >
                Agenda tu reunión inicial gratuita de 30 minutos
              </Button>
            </section>

            {/* Internal Links Section */}
            <section className="py-16">
              <h2 className="text-2xl font-bold mb-6 text-center">Recursos Relacionados</h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">SEO The Woodlands</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Combina PPC con SEO para máximo alcance orgánico
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/seo-the-woodlands'}>
                    Ver SEO Local →
                  </Button>
                </Card>
                
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">Casos de Éxito PPC</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Resultados reales de campañas optimizadas
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/casos-de-exito'}>
                    Ver resultados →
                  </Button>
                </Card>
                
                <Card className="p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2">Blog de Marketing</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Estrategias y tendencias de publicidad digital
                  </p>
                  <Button variant="link" onClick={() => window.location.href = '/blog'}>
                    Leer artículos →
                  </Button>
                </Card>
              </div>
            </section>
          </SEOArticleStructure>
        </main>

        <KnowledgeConnections path="/google-ads-the-woodlands" language={language} />

        <Footer language={language} />
      </div>
    </>
  );
};

export default GoogleAdsTheWoodlandsPage;
